import React, { useState, useEffect } from 'react';
import { 
  Bot, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Send, 
  AlertCircle,
  Mail,
  Calendar,
  User,
  Filter,
  RefreshCw,
  ChevronRight,
  MessageSquare,
  Zap
} from 'lucide-react';
import Modal from '../components/Modal';
import { api } from '../utils/api';

const MaaikePage = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [taskFeedback, setTaskFeedback] = useState('');
  const [aiConfig, setAiConfig] = useState({
    model: 'o3-mini',
    reasoning_effort: 'medium',
    temperature: 0.7
  });

  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    try {
      setLoading(true);
      const response = await api.maaike.getTasks();
      setTasks(response.tasks || []);
    } catch (error) {
      console.error('Error loading tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTaskIcon = (type) => {
    switch(type) {
      case 'email': return <Mail className="text-blue-500" size={20} />;
      case 'event': return <Calendar className="text-green-500" size={20} />;
      case 'contact': return <User className="text-purple-500" size={20} />;
      case 'suggestion': return <Zap className="text-yellow-500" size={20} />;
      default: return <MessageSquare className="text-[#888888]" size={20} />;
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'pending':
        return <span className="flex items-center gap-1 text-yellow-500">
          <Clock size={16} /> Pending Approval
        </span>;
      case 'approved':
        return <span className="flex items-center gap-1 text-green-500">
          <CheckCircle size={16} /> Approved
        </span>;
      case 'rejected':
        return <span className="flex items-center gap-1 text-red-500">
          <XCircle size={16} /> Rejected
        </span>;
      case 'executed':
        return <span className="flex items-center gap-1 text-[#D4A574]">
          <CheckCircle size={16} /> Executed
        </span>;
      default:
        return null;
    }
  };

  const handleApproveTask = async (taskId) => {
    try {
      await api.maaike.approveTask(taskId, { feedback: taskFeedback });
      await loadTasks();
      setSelectedTask(null);
      setTaskFeedback('');
    } catch (error) {
      console.error('Error approving task:', error);
    }
  };

  const handleRejectTask = async (taskId) => {
    try {
      await api.maaike.rejectTask(taskId, { feedback: taskFeedback });
      await loadTasks();
      setSelectedTask(null);
      setTaskFeedback('');
    } catch (error) {
      console.error('Error rejecting task:', error);
    }
  };

  const filteredTasks = tasks.filter(task => {
    if (filterStatus === 'all') return true;
    return task.status === filterStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Bot className="mx-auto text-[#D4A574] mb-4 animate-pulse" size={48} />
          <p className="text-[#888888]">Loading Maaike's tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-light mb-2 flex items-center gap-3">
            <Bot className="text-[#D4A574]" size={32} />
            Maaike
          </h1>
          <p className="text-[#888888]">AI-powered workflow automation and task management</p>
        </div>
        <button 
          onClick={loadTasks}
          className="btn-secondary flex items-center gap-2"
        >
          <RefreshCw size={20} />
          Refresh
        </button>
      </div>

      {/* AI Configuration Card */}
      <div className="card-dark p-6">
        <h2 className="text-xl font-light mb-4">AI Configuration</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-[#888888] mb-2">Model</label>
            <select
              value={aiConfig.model}
              onChange={(e) => setAiConfig({...aiConfig, model: e.target.value})}
              className="input-dark w-full"
            >
              <option value="o3-mini">O3 Mini (Fast)</option>
              <option value="o3">O3 Standard</option>
              <option value="o3-pro">O3 Pro (Best)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-[#888888] mb-2">Reasoning Effort</label>
            <select
              value={aiConfig.reasoning_effort}
              onChange={(e) => setAiConfig({...aiConfig, reasoning_effort: e.target.value})}
              className="input-dark w-full"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-[#888888] mb-2">Temperature</label>
            <input
              type="number"
              min="0"
              max="1"
              step="0.1"
              value={aiConfig.temperature}
              onChange={(e) => setAiConfig({...aiConfig, temperature: parseFloat(e.target.value)})}
              className="input-dark w-full"
            />
          </div>
        </div>
      </div>

      {/* Task Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card-dark text-center py-4">
          <p className="text-2xl font-light">{tasks.filter(t => t.status === 'pending').length}</p>
          <p className="text-sm text-[#888888]">Pending</p>
        </div>
        <div className="card-dark text-center py-4">
          <p className="text-2xl font-light text-green-500">
            {tasks.filter(t => t.status === 'approved').length}
          </p>
          <p className="text-sm text-[#888888]">Approved</p>
        </div>
        <div className="card-dark text-center py-4">
          <p className="text-2xl font-light text-[#D4A574]">
            {tasks.filter(t => t.status === 'executed').length}
          </p>
          <p className="text-sm text-[#888888]">Executed</p>
        </div>
        <div className="card-dark text-center py-4">
          <p className="text-2xl font-light text-red-500">
            {tasks.filter(t => t.status === 'rejected').length}
          </p>
          <p className="text-sm text-[#888888]">Rejected</p>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-4">
        <Filter className="text-[#888888]" size={20} />
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="input-dark"
        >
          <option value="all">All Tasks</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="executed">Executed</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {/* Tasks List */}
      <div className="space-y-4">
        {filteredTasks.length === 0 ? (
          <div className="card-dark text-center py-12">
            <Bot className="mx-auto text-[#333333] mb-4" size={48} />
            <p className="text-[#888888]">No tasks found</p>
          </div>
        ) : (
          filteredTasks.map((task) => (
            <div key={task.id} className="card-dark hover:border-[#2D6B42] transition-all">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4 flex-1">
                  <div className="p-2 bg-[#0A0A0A] rounded-lg">
                    {getTaskIcon(task.type)}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-medium mb-1">{task.title}</h3>
                    <p className="text-sm text-[#888888] mb-2">{task.description}</p>
                    <div className="flex items-center gap-4 text-sm">
                      {getStatusBadge(task.status)}
                      <span className="text-[#888888]">
                        {new Date(task.created_at).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
                {task.status === 'pending' && (
                  <button 
                    onClick={() => setSelectedTask(task)}
                    className="btn-primary flex items-center gap-2"
                  >
                    Review
                    <ChevronRight size={16} />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Task Review Modal */}
      <Modal
        isOpen={!!selectedTask}
        onClose={() => {
          setSelectedTask(null);
          setTaskFeedback('');
        }}
        title="Review Task"
        maxWidth="max-w-2xl"
      >
        {selectedTask && (
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-[#0A0A0A] rounded-lg">
                {getTaskIcon(selectedTask.type)}
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-medium mb-2">{selectedTask.title}</h3>
                <p className="text-[#888888]">{selectedTask.description}</p>
              </div>
            </div>

            {/* Task Details */}
            <div className="bg-[#0A0A0A] p-4 rounded-lg space-y-3">
              <h4 className="font-medium mb-2">Task Details</h4>
              <pre className="text-sm text-[#888888] whitespace-pre-wrap">
                {JSON.stringify(selectedTask.data, null, 2)}
              </pre>
            </div>

            {/* Feedback Input */}
            <div>
              <label className="block text-sm text-[#888888] mb-2">
                Feedback for Maaike (optional)
              </label>
              <textarea
                value={taskFeedback}
                onChange={(e) => setTaskFeedback(e.target.value)}
                placeholder="Provide any adjustments or comments..."
                rows={3}
                className="input-dark w-full"
              />
            </div>

            {/* AI Context */}
            {selectedTask.ai_context && (
              <div className="bg-[#0A0A0A] p-4 rounded-lg">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Bot size={16} />
                  AI Context
                </h4>
                <p className="text-sm text-[#888888]">{selectedTask.ai_context}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-[#333333]">
              <button
                onClick={() => handleApproveTask(selectedTask.id)}
                className="btn-primary flex items-center gap-2"
              >
                <CheckCircle size={16} />
                Approve & Execute
              </button>
              <button
                onClick={() => handleRejectTask(selectedTask.id)}
                className="btn-secondary text-red-500 hover:bg-red-500/10 flex items-center gap-2"
              >
                <XCircle size={16} />
                Reject
              </button>
              <button
                onClick={() => {
                  setSelectedTask(null);
                  setTaskFeedback('');
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Info Section */}
      <div className="card-dark p-6 mt-8">
        <div className="flex items-start gap-3">
          <AlertCircle className="text-[#D4A574] mt-1" size={20} />
          <div>
            <h3 className="font-medium mb-2">About Maaike</h3>
            <p className="text-sm text-[#888888] mb-2">
              Maaike is your AI-powered assistant that helps automate workflows, generate emails, 
              and provide intelligent suggestions based on your CRM data and event context.
            </p>
            <p className="text-sm text-[#888888]">
              All tasks require approval before execution to ensure quality and maintain control 
              over automated actions. Maaike uses OpenAI's o3 model for advanced reasoning capabilities.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaaikePage;